/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import {
  getCountryCallingCode,
  isSupportedCountry,
  type CountryCode,
} from "libphonenumber-js";
import { normalizePhone } from "@/lib/phone";

/**
 * Parse arbitrary phone input into strict E.164. Returns null if the input is
 * empty, unparsable, or fails libphonenumber's `isValid` heuristic.
 *
 * The default region is US — MyAmbits users are US-based and an unprefixed
 * "415 555 1234" should parse. Users outside the US should type the leading
 * country code (e.g. "+44 …") and libphonenumber will pick that up regardless
 * of the default region.
 *
 * Thin wrapper over the shared `normalizePhone` primitive (src/lib/phone.ts)
 * — kept here so the many existing callers (contact-sync matching, phone
 * auth) don't need to switch to the `{e164}|{error}` result shape.
 */
export function normalizeToE164(
  raw: string,
  defaultRegion: CountryCode = "US",
): string | null {
  const result = normalizePhone(raw, defaultRegion);
  return "e164" in result ? result.e164 : null;
}

/**
 * Derive a libphonenumber CountryCode from a BCP-47 locale string
 * (e.g. "en-GB" → "GB", "fr-CA" → "CA"). Returns null when the locale
 * carries no region subtag or the region is not a phone-supported
 * country. Used by contact-sync to add the device's home region as a
 * fallback parse region (B12) so a contact stored in bare national
 * format for the user's own country still resolves to E.164.
 */
export function regionFromLocale(
  locale: string | null | undefined,
): CountryCode | null {
  if (typeof locale !== "string" || locale.length === 0) return null;
  const parts = locale.split(/[-_]/);
  // Region subtag is the 2-letter ALL-CAPS-ish segment after the language.
  const region = parts
    .slice(1)
    .find((p) => /^[A-Za-z]{2}$/.test(p))
    ?.toUpperCase();
  if (!region) return null;
  return isSupportedCountry(region) ? (region as CountryCode) : null;
}

/**
 * Normalize ALL of a contact's phone numbers to E.164, trying each
 * candidate region in order until one parses. Dedupes the result.
 *
 * B8: a contact's MyAmbits number may be their 2nd/3rd entry, not the
 * first — so we resolve every number, not just phones[0].
 * B12: bare national numbers can't be parsed without a region hint, so
 * the caller passes an ordered region list (e.g. ["US", deviceRegion]);
 * the first region that yields a valid number wins for that raw input.
 * A number that already carries a "+<code>" prefix parses regardless of
 * region, so explicit-international contacts are unaffected by ordering.
 */
export function normalizeContactPhones(
  rawPhones: readonly string[],
  regions: readonly CountryCode[] = ["US"],
): string[] {
  const tryRegions = regions.length > 0 ? regions : (["US"] as const);
  const out = new Set<string>();
  for (const raw of rawPhones) {
    for (const region of tryRegions) {
      const e164 = normalizeToE164(raw, region);
      if (e164) {
        out.add(e164);
        break; // first region that parses this number wins
      }
    }
  }
  return Array.from(out);
}

/**
 * A country option for the phone-entry country-code selector. `region` is the
 * ISO-3166 code libphonenumber uses (the default region for parsing); `dialCode`
 * is the "+NN" prefix shown to the user; `flag` is the emoji.
 */
export interface CountryOption {
  region: CountryCode;
  dialCode: string;
  name: string;
  flag: string;
}

/**
 * Curated country list for the onboarding phone-entry selector. The US is first
 * (the launch market — the selector defaults to +1), followed by the other
 * common regions. This is intentionally short; libphonenumber still parses any
 * number whose input carries an explicit "+<code>" prefix regardless of the
 * selected region, so the list is a convenience default, not a hard gate.
 */
export const COUNTRY_OPTIONS: readonly CountryOption[] = [
  { region: "US", dialCode: "+1", name: "United States", flag: "🇺🇸" },
  { region: "CA", dialCode: "+1", name: "Canada", flag: "🇨🇦" },
  { region: "GB", dialCode: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { region: "IN", dialCode: "+91", name: "India", flag: "🇮🇳" },
  { region: "AU", dialCode: "+61", name: "Australia", flag: "🇦🇺" },
  { region: "AE", dialCode: "+971", name: "United Arab Emirates", flag: "🇦🇪" },
  { region: "SG", dialCode: "+65", name: "Singapore", flag: "🇸🇬" },
  { region: "DE", dialCode: "+49", name: "Germany", flag: "🇩🇪" },
  { region: "FR", dialCode: "+33", name: "France", flag: "🇫🇷" },
] as const;

/** Default onboarding country (United States / +1) — USA-first launch. */
export const DEFAULT_COUNTRY: CountryOption = COUNTRY_OPTIONS[0];

/** "+NN" dial code for an ISO region, or null if the region is unknown. */
export function dialCodeForRegion(region: CountryCode): string | null {
  try {
    return `+${getCountryCallingCode(region)}`;
  } catch {
    return null;
  }
}
